import React from 'react';
import { logApp } from '../utils/logger';

type ErrorBoundaryProps = {
	children: React.ReactNode;
	fallback?: React.ReactNode;
	onError?: (error: Error, info: React.ErrorInfo) => void;
	/** If true, shows a full-page error UI suitable for app-level errors */
	fullPage?: boolean;
};

type ErrorBoundaryState = {
	hasError: boolean;
	error?: Error;
	errorInfo?: React.ErrorInfo;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		this.setState({ errorInfo: info });
		if (this.props.onError) this.props.onError(error, info);
		logApp.error('ErrorBoundary caught an error:', error, info);
	}

	handleReset = () => {
		this.setState({ hasError: false, error: undefined, errorInfo: undefined });
	};

	render() {
		if (this.state.hasError) {
			// Use custom fallback if provided
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Full-page error UI for app-level errors
			if (this.props.fullPage) {
				return (
					<div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
						<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
							<div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
								<svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
								</svg>
							</div>
							<h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
								Something went wrong
							</h1>
							<p className="text-gray-600 text-center mb-6">
								The application encountered an unexpected error. Please try refreshing the page.
							</p>
							{process.env.NODE_ENV === 'development' && this.state.error && (
								<details className="mb-4 p-3 bg-gray-100 rounded text-xs">
									<summary className="cursor-pointer font-medium text-gray-700">Error Details</summary>
									<pre className="mt-2 text-red-600 overflow-auto">{this.state.error.toString()}</pre>
									{this.state.errorInfo && (
										<pre className="mt-2 text-gray-600 overflow-auto">{this.state.errorInfo.componentStack}</pre>
									)}
								</details>
							)}
							<div className="flex gap-3">
								<button
									onClick={() => window.location.href = '/'}
									className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
								>
									Reload Application
								</button>
								<button
									onClick={this.handleReset}
									className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
								>
									Try Again
								</button>
							</div>
						</div>
					</div>
				);
			}

			// Default inline error UI for component-level errors
			return (
				<div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-md">
					<div className="flex items-start">
						<svg className="w-5 h-5 text-red-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<div className="flex-1">
							<p className="font-medium">An unexpected error occurred</p>
							{process.env.NODE_ENV === 'development' && this.state.error && (
								<details className="mt-2">
									<summary className="cursor-pointer text-sm">Error Details</summary>
									<pre className="mt-1 text-xs overflow-auto">{this.state.error.toString()}</pre>
								</details>
							)}
							<button
								onClick={this.handleReset}
								className="mt-2 text-sm underline hover:no-underline"
							>
								Try again
							</button>
						</div>
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}

export default ErrorBoundary;
